import { FunctionComponent } from 'react'
import { ChildsSlots } from '@/types/StaffRate'
import { Absence, OccasionalSlot, RegularCancellationRequest, RegularSlot } from '@/types/Slots'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import moment from 'moment/moment'
import { defineChildSlots, TimBarSlots } from '../../TimBarSlots/defineSlots'
import styles from '../DraftingStaffSlots/draftingSlots.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { ChildCare } from '@mui/icons-material'
import TimeBarSlots from '../../TimBarSlots/timeBarSlots'

interface DraftingChildSlotsProps {
    childSlots: ChildsSlots
    opening: string
    closing: string
    setSelectedEvent: (event: EventCalendar | null) => void
    setShowSlotDialogOpen: (open: boolean) => void
    date: string
}

const DraftingChildSlots: FunctionComponent<DraftingChildSlotsProps> = ({
    childSlots,
    opening,
    closing,
    setSelectedEvent,
    setShowSlotDialogOpen,
    date
}: DraftingChildSlotsProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const childHours: number =
        childSlots.regularSlots.reduce((total: number, slot: RegularSlot) => {
            return total + moment(slot.end, 'HH:mm').diff(moment(slot.start, 'HH:mm'), 'hours')
        }, 0) +
        childSlots.occasionalSlots.reduce((total: number, slot: OccasionalSlot) => {
            return total + moment(slot.end, 'HH:mm').diff(moment(slot.start, 'HH:mm'), 'hours')
        }, 0)

    const totalMinutes: number = moment.duration(moment(closing, 'HH:mm').diff(moment(opening, 'HH:mm'))).asMinutes()

    const timBarSlots: TimBarSlots[] = defineChildSlots(
        childSlots.occasionalSlots,
        childSlots.regularSlots,
        opening,
        closing,
        date
    )

    function handleSelectSlot(slot: TimBarSlots): void {
        const id: number | undefined = slot.id
        if (id) {
            const type: CalendarEventType | undefined = timBarSlots.find((slot: TimBarSlots) => slot.id === id)!.type

            const childOccasionalSlot: OccasionalSlot | undefined = childSlots.occasionalSlots.find(
                (slot: OccasionalSlot): boolean => slot.id === id
            )
            const childRegularSlot: RegularSlot | undefined = childSlots.regularSlots.find(
                (slot: RegularSlot): boolean => slot.id === id
            )
            const childRegularCancellationRequest: RegularCancellationRequest | undefined = childRegularSlot
                ? childRegularSlot.cancellationRequests.find(
                      (request: RegularCancellationRequest): boolean =>
                          moment(request.date, 'DD-MM-YYYY').format('DD-MM-YYYY') === date
                  )
                : undefined
            const childRegularAbsence: RegularSlot | undefined = childRegularSlot?.absence
                ? childRegularSlot
                : undefined

            switch (type) {
                case CalendarEventType.VALIDATED:
                    if (childOccasionalSlot) {
                        setSelectedEvent({
                            title: childOccasionalSlot.childName + ' ' + childOccasionalSlot.childComplement,
                            start: moment(childOccasionalSlot.start, 'HH:mm').toDate(),
                            end: moment(childOccasionalSlot.end, 'HH:mm').toDate(),
                            number: '',
                            type: CalendarEventType.VALIDATED,
                            childId: childOccasionalSlot.childId || undefined,
                            placeId: childOccasionalSlot.placeId ? childOccasionalSlot.placeId : undefined,
                            sectionId: childOccasionalSlot.sectionId ? childOccasionalSlot.sectionId : undefined,
                            slots: [childOccasionalSlot]
                        })
                        setShowSlotDialogOpen(true)
                    }
                    break
                case CalendarEventType.REGULAR:
                    if (childRegularSlot) {
                        setSelectedEvent({
                            title: childRegularSlot.childName + ' ' + childRegularSlot.childComplement,
                            start: moment(date + ' ' + childRegularSlot.start, 'DD-MM-YYYY HH:mm').toDate(),
                            end: moment(date + ' ' + childRegularSlot.end, 'DD-MM-YYYY HH:mm').toDate(),
                            number: '',
                            type: type,
                            childId: childRegularSlot.childId || undefined,
                            sectionId: childRegularSlot.sectionId ? childRegularSlot.sectionId : undefined,
                            slots: [childRegularSlot]
                        })
                        setShowSlotDialogOpen(true)
                    }
                    break
                case CalendarEventType.REGULAR_REQUEST:
                    if (childRegularSlot && childRegularCancellationRequest) {
                        setSelectedEvent({
                            title: childRegularSlot.childName + ' ' + childRegularSlot.childComplement,
                            start: moment(date + ' ' + childRegularSlot.start, 'DD-MM-YYYY HH:mm:ss').toDate(),
                            end: moment(date + ' ' + childRegularSlot.end, 'DD-MM-YYYY HH:mm:ss').toDate(),
                            number: '',
                            type: type,
                            childId: childRegularSlot.childId || undefined,
                            sectionId: childRegularSlot.sectionId ? childRegularSlot.sectionId : undefined,
                            slots: [
                                {
                                    ...childRegularSlot,
                                    id: childRegularCancellationRequest?.id,
                                    start: moment(childRegularCancellationRequest?.start, 'HH:mm:ss').format('HH:mm'),
                                    end: moment(childRegularCancellationRequest?.end, 'HH:mm:ss').format('HH:mm'),
                                    regularSlotId: childRegularSlot.id,
                                    date: childRegularCancellationRequest?.date,
                                    reason: childRegularCancellationRequest?.reason,
                                    regularSlotStart: childRegularSlot.start,
                                    regularSlotEnd: childRegularSlot.end
                                }
                            ] as RegularCancellationRequest[]
                        })
                        setShowSlotDialogOpen(true)
                    }
                    break
                case CalendarEventType.ABSENCE:
                    if (childRegularSlot && childRegularAbsence) {
                        setSelectedEvent({
                            title: childRegularSlot.childName + ' ' + childRegularSlot.childComplement,
                            start: moment(date + ' ' + childRegularSlot.start, 'DD-MM-YYYY HH:mm:ss').toDate(),
                            end: moment(date + ' ' + childRegularSlot.end, 'DD-MM-YYYY HH:mm:ss').toDate(),
                            number: '',
                            type: type,
                            childId: childRegularSlot.childId || undefined,
                            sectionId: childRegularSlot.sectionId ? childRegularSlot.sectionId : undefined,
                            slots: [
                                {
                                    ...childRegularSlot,
                                    regularSlotId: childRegularSlot.id,
                                    date: childRegularAbsence?.date
                                }
                            ] as Absence[]
                        })
                        setShowSlotDialogOpen(true)
                    }
                    break
                default:
                    setShowSlotDialogOpen(false)
                    setSelectedEvent(null)
            }
            setShowSlotDialogOpen(true)
        } else {
            setShowSlotDialogOpen(false)
            setSelectedEvent(null)
        }
    }

    return (
        <>
            <div className={styles.Line}>
                <div className={styles.StaffProfile}>
                    <ButtonIcon
                        icon={<ChildCare />}
                        className={styles.ButtonIcon}
                        onClick={() => navigate(`/childs?child=${childSlots.childId}`)}
                    />
                    <div className={styles.Info}>
                        <span className={styles.Name}>{childSlots.childName}</span>
                        <span className={styles.Hours}>
                            {childHours} {childHours > 1 ? t('hours') : t('hour')}
                        </span>
                    </div>
                </div>
                <div className={styles.Slots}>
                    <TimeBarSlots
                        timBarSlots={timBarSlots}
                        totalMinutes={totalMinutes}
                        handleSelect={handleSelectSlot}
                    />
                </div>
            </div>
        </>
    )
}

export default DraftingChildSlots
